/**
 * Traduction des alertes pour un lecteur NON TECHNIQUE — module pur.
 *
 * Le back-office est lu par des opérateurs TROC et des responsables COJOJ, en
 * pleine exploitation, souvent la nuit. `fleet_snapshot_stale` ne veut rien
 * dire pour eux ; « Le suivi des bus ne se met plus à jour » veut dire quelque
 * chose. L'identifiant technique reste la clé en base et dans l'API — il ne
 * franchit simplement jamais l'écran.
 *
 * Trois informations par règle, et pas une de plus :
 *  · **quoi** — le titre, ce qui ne va pas ;
 *  · **conséquence** — ce que ça change pour les athlètes, parce que c'est ce
 *    qui permet de décider si on se lève à 3 h du matin ;
 *  · **que faire** — la première action, sans laquelle une alerte n'est qu'une
 *    mauvaise nouvelle. C'est l'ajout qui transforme un message en consigne.
 */

export interface AlertGuidance {
  /** Titre court, en français courant. */
  label: string;
  /** Ce que ça change concrètement pour les athlètes ou l'exploitation. */
  consequence: string;
  /** Première action à mener. Une phrase, un verbe à l'infinitif. */
  action: string;
}

const CATALOG: Record<string, AlertGuidance> = {
  fleet_offline: {
    label: 'Aucun bus ne circule sur le réseau',
    consequence:
      "Les athlètes ouvrent l'application et ne voient aucun bus, ni sur la carte ni dans les temps d'attente.",
    action:
      "Vérifier auprès de l'exploitant que les bus sont bien en service et que leurs boîtiers de localisation sont allumés.",
  },
  fleet_snapshot_stale: {
    label: 'Le suivi des bus ne se met plus à jour',
    consequence:
      "Les compteurs de flotte affichés ici sont figés tout en ayant l'air à jour : on ne sait plus combien de bus roulent réellement.",
    action:
      "Prévenir l'équipe technique : le relevé automatique du back-office ne tourne plus (ou la source des positions n'est pas configurée sur ce serveur) et doit être relancé.",
  },
  ingest_silent: {
    label: "L'application n'envoie plus rien",
    consequence:
      "Les chiffres d'usage (utilisateurs, sessions, alertes posées) vont se figer sans que rien ne le signale.",
    action:
      "Prévenir l'équipe technique et vérifier que le serveur du back-office accepte encore les envois des téléphones.",
  },
  error_rate: {
    label: "L'application signale trop d'erreurs",
    consequence:
      'Les athlètes rencontrent probablement des blocages ou des écrans en erreur, sur une partie du parc.',
    action:
      "Prévenir l'équipe applicative : une version récente de l'application est le premier suspect.",
  },
  lenz_down: {
    label: 'Le fournisseur des positions de bus ne répond plus',
    consequence:
      "La carte de l'application affiche des bus figés, ou aucun bus. Le reste du tableau de bord reste fiable.",
    action:
      "Contacter l'exploitant de la passerelle Lenz / SAEIV, qui fournit les lignes et les positions.",
  },
};

/**
 * Repli pour une règle ajoutée côté serveur avant que ce fichier ne la
 * connaisse. Il ne montre PAS l'identifiant technique : mieux vaut une phrase
 * générique qu'un mot de code au milieu d'un écran d'exploitation.
 */
const FALLBACK: AlertGuidance = {
  label: 'Anomalie détectée sur le service',
  consequence: "Un seuil de surveillance a été dépassé et n'est pas encore revenu à la normale.",
  action: "Prévenir l'équipe technique du back-office.",
};

export function guidanceFor(rule: string, serverTitle?: string): AlertGuidance {
  const known = CATALOG[rule];
  if (known) return known;
  // Le serveur rédige déjà ses titres en français : s'il en fournit un, il vaut
  // mieux que notre phrase générique.
  return serverTitle && serverTitle.trim() !== '' && !/[a-z]+_[a-z]+/.test(serverTitle)
    ? { ...FALLBACK, label: serverTitle }
    : FALLBACK;
}

// ─── Gravité ────────────────────────────────────────────────────────────────

export type AlertSeverity = 'info' | 'warning' | 'critical';

export function normalizeSeverity(value: string): AlertSeverity {
  return value === 'critical' || value === 'warning' || value === 'info' ? value : 'warning';
}

/** Mots, pas codes : « critical » n'est pas français. */
export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  critical: 'Critique',
  warning: 'À surveiller',
  info: 'Information',
};

export const SEVERITY_ORDER: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };
